import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { RoomsSchedule, ScheduleTimeBlock, WEEK_DAY_LABELS } from '../types';
import SectionCell from './SectionCell';
import useStyles from '../styles';

type ScheduleGridProps = {
  schedule: RoomsSchedule;
  onSectionClick: (sectionId: number, mipreuId: number) => void;
  printTitle: string;
};

const isEmptyBlock = (block: ScheduleTimeBlock, weekDays: string[]): boolean => block.rooms
  .every((room) => weekDays.every((weekDay) => (room.cells[weekDay] ?? []).length === 0));

function ScheduleGrid({ schedule, onSectionClick, printTitle }: ScheduleGridProps) {
  const classes = useStyles();
  const { weekDays, timeBlocks, roomsCount } = schedule;
  const hasFixedRooms = Boolean(roomsCount);

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table className={classes.table} size='small'>
        <colgroup>
          <col className={classes.hourCol} />
          <col className={classes.roomCol} />
          {weekDays.map((weekDay) => (
            <col key={`col-${weekDay}`} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow className={classes.printTitleRow}>
            <TableCell
              className={classes.printTitleCell}
              colSpan={weekDays.length + 2}
            >
              {printTitle}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell className={`${classes.headerCell} ${classes.hourHeaderCell}`}>Hora</TableCell>
            <TableCell className={`${classes.headerCell} ${classes.roomHeaderCell}`}>Sala</TableCell>
            {weekDays.map((weekDay) => (
              <TableCell key={`header-${weekDay}`} className={classes.headerCell}>
                {WEEK_DAY_LABELS[weekDay] ?? weekDay}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        {timeBlocks.map((block, blockIndex) => (
          <TableBody
            key={`block-${block.startTime}`}
            className={isEmptyBlock(block, weekDays) ? classes.emptyBlock : undefined}
          >
            {block.rooms.map((room, roomIndex) => (
              <TableRow key={`block-${block.startTime}-room-${room.classroom}`}>
                <TableCell
                  className={`${classes.hourCell} ${roomIndex < block.rooms.length - 1 ? classes.hourCellContinues : ''}`}
                >
                  <span className={roomIndex === 0 ? undefined : classes.hourRepeatText}>
                    {block.startTime}
                  </span>
                </TableCell>
                <TableCell
                  className={`${classes.roomCell} ${room.overCapacity ? classes.overCapacityRoomCell : ''}`}
                  title={room.overCapacity ? 'Fila fuera del cupo de salas de la sede' : undefined}
                >
                  {hasFixedRooms ? `Sala ${room.classroom}` : (room.classroom || 'Sin sala')}
                </TableCell>
                {weekDays.map((weekDay) => (
                  <TableCell
                    key={`cell-${block.startTime}-${room.classroom}-${weekDay}`}
                    className={classes.dayCell}
                  >
                    <SectionCell
                      sections={room.cells[weekDay] ?? []}
                      onSectionClick={onSectionClick}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {blockIndex < timeBlocks.length - 1 && (
              <TableRow>
                <TableCell
                  className={classes.blockSpacer}
                  colSpan={weekDays.length + 2}
                />
              </TableRow>
            )}
          </TableBody>
        ))}
      </Table>
    </TableContainer>
  );
}

export default ScheduleGrid;
